// src/pages/PrivacyPolicy.tsx
// Public page — no auth required.
// Complies with Ley N° 29733 — Ley de Protección de Datos Personales del Perú.

import { Shield, ArrowLeft, Mail, Clock, Globe, FileText, Database, Users, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CONTACT } from '../constants/config';
import { useTheme } from '../context/ThemeContext';
import { colors } from '../utils/theme';
import { useLang } from '../context/LangContext';

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'TryQuiPus';
const LEGAL_NAME = 'Quipus Tech LLC';

const SECTION_TITLE = 'text-base font-semibold mb-3 flex items-center gap-2';
const BODY = 't-small leading-relaxed';

export default function PrivacyPolicy() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const isEs = lang === 'es';

  const cardStyle: React.CSSProperties = {
    background: c.card,
    border: `1px solid ${c.border}`,
  };

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: c.canvas }}>
      {/* ES | EN toggle */}
      <div className="fixed top-4 right-4 flex items-center gap-1 rounded-full shadow px-1 py-1"
        style={{ background: c.card, border: `1px solid ${c.border}` }}>
        <button
          onClick={() => setLang('es')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={isEs ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          ES
        </button>
        <button
          onClick={() => setLang('en')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={!isEs ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          EN
        </button>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">

        {/* Back */}
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm transition-colors"
          style={{ color: c.textSub }}
        >
          <ArrowLeft size={15} />
          {isEs ? 'Volver' : 'Back'}
        </button>

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-[2rem] bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center shrink-0 mt-0.5">
            <Shield size={20} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: c.textPrimary }}>
              {isEs ? 'Política de Privacidad' : 'Privacy Policy'}
            </h1>
            <p className="text-sm mt-1" style={{ color: c.textSub }}>
              {APP_NAME}™ — {isEs ? 'Versión 1.2 · Vigente desde mayo 2026' : 'Version 1.2 · Effective since May 2026'}
            </p>
            <p className="t-2xs mt-1" style={{ color: c.textMuted }}>
              {isEs
                ? 'Conforme a la Ley N° 29733 — Ley de Protección de Datos Personales del Perú'
                : 'Pursuant to Law N° 29733 — Personal Data Protection Law of Peru'}
            </p>
          </div>
        </div>

        {/* Sección 1: Qué datos recopilamos */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Database size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '¿Qué datos recopilamos?' : 'What data do we collect?'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Recopilamos los datos estrictamente necesarios para prestar el servicio contable:'
              : 'We collect strictly necessary data to provide the accounting service:'}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'RUC y datos fiscales:' : 'RUC and tax data:'}
              </strong>{' '}
              {isEs
                ? 'número de RUC, razón social y régimen tributario de tu empresa.'
                : "your company's RUC number, legal name, and tax regime."}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Datos financieros:' : 'Financial data:'}
              </strong>{' '}
              {isEs
                ? 'facturas de compras y ventas, asientos contables, balances y libros PLE/SIRE generados en la plataforma.'
                : 'purchase and sales invoices, accounting entries, balance sheets, and PLE/SIRE books generated on the platform.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Datos de cuenta:' : 'Account data:'}
              </strong>{' '}
              {isEs
                ? `correo electrónico, contraseña (cifrada por nuestro proveedor de autenticación, no accesible para ${LEGAL_NAME}).`
                : `email address, password (encrypted by our authentication provider, not accessible to ${LEGAL_NAME}).`}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Actividad en plataforma:' : 'Platform activity:'}
              </strong>{' '}
              {isEs
                ? 'registros de acceso (timestamp, IP hasheada), acciones sobre datos contables y eventos de auditoría internos.'
                : 'access logs (timestamp, hashed IP), actions on accounting data, and internal audit events.'}
            </li>
          </ul>
          <p className="t-2xs mt-2" style={{ color: c.textMuted }}>
            {isEs
              ? 'No recopilamos datos biométricos, datos sensibles según Art. 13 de la Ley 29733, ni información de menores de edad.'
              : 'We do not collect biometric data, sensitive data under Art. 13 of Law 29733, or information about minors.'}
          </p>
        </div>

        {/* Sección 2: Dónde se almacenan */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Globe size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '¿Dónde se almacenan?' : 'Where is it stored?'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>Tus datos se almacenan en{' '}<strong style={{ color: c.textPrimary }}>servidores en EE.UU. con certificación SOC 2/ISO 27001</strong>, con cifrado en reposo (AES-256) y en tránsito (TLS 1.3).</>
              : <>Your data is stored on{' '}<strong style={{ color: c.textPrimary }}>U.S.-based servers with SOC 2/ISO 27001 certification</strong>, with encryption at rest (AES-256) and in transit (TLS 1.3).</>}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Cada empresa está aislada mediante Row Level Security (RLS): ningún usuario puede acceder a datos de otra empresa. El aislamiento es técnico y obligatorio a nivel de base de datos.'
              : "Each company is isolated via Row Level Security (RLS): no user can access another company's data. Isolation is enforced at the database level."}
          </p>
          <div className="bg-cyan-400/5 border border-cyan-400/15 rounded-xl px-4 py-3">
            <p className="t-sm font-medium" style={{ color: c.info }}>
              {isEs ? 'Roadmap de región' : 'Region roadmap'}
            </p>
            <p className="t-2xs mt-1" style={{ color: c.textSub }}>
              {isEs
                ? 'Tenemos planificado migrar los datos de clientes peruanos a servidores en Brasil (región São Paulo) en nuestro roadmap 2026, para reducir latencia y reforzar soberanía de datos regional.'
                : 'We plan to migrate Peruvian client data to servers in Brazil (São Paulo region) in our 2026 roadmap, to reduce latency and reinforce regional data sovereignty.'}
            </p>
          </div>
        </div>

        {/* Sección 3: Para qué los usamos */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <FileText size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '¿Para qué los usamos?' : 'What do we use it for?'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs ? 'Usamos tus datos exclusivamente para:' : 'We use your data exclusively for:'}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Generar libros contables PCGE (Libro Diario, Libro Mayor, Balance de Comprobación).'
                : 'Generating PCGE accounting books (General Journal, General Ledger, Trial Balance).'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Cumplimiento tributario SUNAT: Registros de Compras 8.1 y Ventas 14.1 (PLE/SIRE), cálculo de IGV y pago a cuenta de Renta.'
                : 'SUNAT tax compliance: Purchase Register 8.1 and Sales Register 14.1 (PLE/SIRE), IGV calculation and income tax prepayments.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Mejora del servicio: análisis agregados y anónimos de uso de la plataforma para mejorar funcionalidades.'
                : 'Service improvement: aggregated and anonymous usage analysis to improve platform features.'}
            </li>
          </ul>
          <p className="t-2xs mt-1" style={{ color: c.textMuted }}>
            <strong style={{ color: c.textSub }}>
              {isEs ? 'No vendemos tus datos.' : 'We do not sell your data.'}
            </strong>{' '}
            {isEs
              ? 'No los compartimos con terceros con fines comerciales. No los usamos para publicidad.'
              : 'We do not share it with third parties for commercial purposes. We do not use it for advertising.'}
          </p>
        </div>

        {/* Sección 4: Retención */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Clock size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '¿Cuánto tiempo los guardamos?' : 'How long do we keep it?'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>Los datos contables y tributarios se conservan por un mínimo de{' '}<strong style={{ color: c.textPrimary }}>7 años</strong>, conforme al plazo de prescripción establecido por SUNAT para la revisión de libros y registros contables (Art. 43 del Código Tributario peruano).</>
              : <>Accounting and tax data is retained for a minimum of{' '}<strong style={{ color: c.textPrimary }}>7 years</strong>, pursuant to the statute of limitations established by SUNAT for the review of accounting books and records (Art. 43 of the Peruvian Tax Code).</>}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Los datos de cuenta (email, preferencias de usuario) se conservan mientras la cuenta esté activa. Al solicitar la eliminación, los datos contables pasarán a estado archivado por el período legal mínimo antes de su eliminación definitiva.'
              : 'Account data (email, user preferences) is retained while the account is active. Upon deletion request, accounting data will be archived for the minimum legal period before permanent deletion.'}
          </p>
        </div>

        {/* Sección 5: Tus derechos */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Users size={15} className="text-cyan-400 shrink-0" />
            {isEs ? 'Tus derechos — Ley N° 29733' : 'Your rights — Law N° 29733'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Como titular de datos personales tienes los siguientes derechos reconocidos por la Ley 29733:'
              : 'As a personal data subject, you have the following rights under Law 29733:'}
          </p>
          <div className="grid grid-cols-2 gap-3 mt-1">
            {(isEs
              ? [
                  { title: 'Acceso',         desc: 'Solicitar qué datos tuyos procesamos y con qué finalidad.' },
                  { title: 'Rectificación',  desc: 'Corregir datos incorrectos o incompletos.' },
                  { title: 'Supresión',      desc: 'Solicitar la eliminación de tus datos.' },
                  { title: 'Oposición',      desc: 'Oponerte al tratamiento de tus datos por razones fundadas y legítimas (Art. 19 Ley 29733).' },
                ]
              : [
                  { title: 'Access',         desc: 'Request what data we process about you and for what purpose.' },
                  { title: 'Rectification',  desc: 'Correct inaccurate or incomplete data.' },
                  { title: 'Erasure',        desc: 'Request deletion of your data.' },
                  { title: 'Objection',      desc: 'Object to the processing of your data for legitimate, well-founded reasons (Art. 19 Law 29733).' },
                ]
            ).map(({ title, desc }) => (
              <div key={title} className="rounded-xl p-3" style={{ background: c.subtleBg, border: `1px solid ${c.border}` }}>
                <p className="t-sm font-semibold mb-1" style={{ color: c.info }}>{title}</p>
                <p className="t-2xs leading-relaxed" style={{ color: c.textSub }}>{desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Mail size={13} className="shrink-0" style={{ color: c.textSub }} />
            <p className="t-sm" style={{ color: c.textNormal }}>
              {isEs ? 'Para ejercer estos derechos contacta:' : 'To exercise these rights contact:'}{' '}
              <a href={`mailto:${CONTACT.EMAIL_PRIVACY}`} className="text-cyan-400 hover:underline">
                {CONTACT.EMAIL_PRIVACY}
              </a>
              {' '}—{' '}
              {isEs
                ? 'respondemos en un plazo máximo de 20 días hábiles.'
                : 'we respond within a maximum of 20 business days.'}
            </p>
          </div>
          <div className="bg-cyan-400/5 border border-cyan-400/15 rounded-xl px-4 py-3">
            <p className="t-sm font-medium" style={{ color: c.info }}>
              {isEs ? 'Recurso ante la ANPD' : 'Appeal to the ANPD'}
            </p>
            <p className="t-2xs mt-1" style={{ color: c.textSub }}>
              {isEs
                ? <>Si {LEGAL_NAME} no atiende tu solicitud dentro del plazo legal, puedes presentar una reclamación ante la{' '}<strong style={{ color: c.textSub }}>Autoridad Nacional de Protección de Datos Personales (ANPD)</strong> del Ministerio de Justicia y Derechos Humanos del Perú:{' '}</>
                : <>If {LEGAL_NAME} does not respond to your request within the legal deadline, you may file a complaint with the{' '}<strong style={{ color: c.textSub }}>National Authority for Personal Data Protection (ANPD)</strong> of Peru's Ministry of Justice and Human Rights:{' '}</>}
              <a
                href="https://www.minjus.gob.pe/anpd/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:underline"
              >
                minjus.gob.pe/anpd
              </a>.
            </p>
          </div>
        </div>

        {/* Sección 6: Transferencias internacionales */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Globe size={15} className="text-cyan-400 shrink-0" />
            {isEs ? 'Transferencias internacionales' : 'International transfers'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'El almacenamiento de datos en servidores en EE.UU. fuera del Perú constituye una transferencia internacional conforme al Art. 15 de la Ley N° 29733 y su Reglamento (D.S. N° 003-2013-JUS).'
              : 'Data storage on U.S.-based servers outside of Peru constitutes an international transfer pursuant to Art. 15 of Law N° 29733 and its Regulations (D.S. N° 003-2013-JUS).'}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Nuestros servidores cuentan con certificaciones de seguridad (SOC 2 Type II, ISO 27001) que ofrecen estándares de protección equivalentes a los exigidos por la normativa peruana vigente. Esta transferencia se realiza con el consentimiento expreso del titular recogido durante el registro.'
              : 'Our servers hold security certifications (SOC 2 Type II, ISO 27001) that provide protection standards equivalent to those required by applicable Peruvian law. This transfer is carried out with the express consent of the data subject collected during registration.'}
          </p>
        </div>

        {/* Sección 7: Sub-procesadores */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Shield size={15} className="text-cyan-400 shrink-0" />
            {isEs ? 'Sub-procesadores' : 'Sub-processors'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Para prestar el servicio TryQuiPus™ contamos con los siguientes sub-procesadores que pueden tratar datos en tu nombre:'
              : 'To provide the TryQuiPus™ service, we use the following sub-processors who may process data on your behalf:'}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Infraestructura cloud (EE.UU., SOC 2/ISO 27001):' : 'Cloud infrastructure (U.S., SOC 2/ISO 27001):'}
              </strong>{' '}
              {isEs
                ? 'almacenamiento y autenticación (ver sección "¿Dónde se almacenan?").'
                : 'storage and authentication (see "Where is it stored?" section).'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>Anthropic (Claude API):</strong>{' '}
              {isEs
                ? 'para funcionalidades de inteligencia artificial, utilizamos la API de Anthropic (Claude) como sub-procesador. Los datos procesados no se utilizan para entrenar modelos de IA. Los datos enviados son previamente anonimizados (RUC, razón social y cuentas bancarias se reemplazan por tokens SHA-256 irreversibles antes de salir del navegador).'
                : 'for artificial intelligence features, we use the Anthropic API (Claude) as a sub-processor. Processed data is not used to train AI models. Data sent is previously anonymized (RUC, legal name, and bank accounts are replaced with irreversible SHA-256 tokens before leaving the browser).'}{' '}
              {isEs ? 'Más información:' : 'More info:'}{' '}
              <a
                href="https://www.anthropic.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:underline"
              >
                anthropic.com/privacy
              </a>
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>Resend:</strong>{' '}
              {isEs
                ? 'envío de correos transaccionales (verificación, bienvenida). Solo recibe la dirección de email destino y el contenido del template. No recibe datos contables.'
                : 'sending transactional emails (verification, welcome). It only receives the destination email address and the template content. It does not receive accounting data.'}
            </li>
          </ul>
        </div>

        {/* Sección 8: Datos de clientes de la contadora */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Users size={15} className="text-cyan-400 shrink-0" />
            {isEs ? 'Datos de clientes de la contadora' : "Accountant's client data"}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>Al subir información de sus clientes empresariales en TryQuiPus™, la contadora declara contar con la autorización correspondiente para procesar dicha data en la plataforma. {LEGAL_NAME} actúa como <strong style={{ color: c.textPrimary }}>encargado de tratamiento</strong> en estos casos, conforme al Art. 16 de la Ley N° 29733 y su Reglamento.</>
              : <>By uploading information about their business clients to TryQuiPus™, the accountant declares they hold the appropriate authorization to process such data on the platform. {LEGAL_NAME} acts as the <strong style={{ color: c.textPrimary }}>data processor</strong> in these cases, pursuant to Art. 16 of Law N° 29733 and its Regulations.</>}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? `Los datos de los clientes de la contadora están sujetos a las mismas medidas de seguridad, aislamiento y retención descritas en esta política. No son accesibles por otras cuentas ni por el equipo de ${LEGAL_NAME}, salvo requerimiento legal expreso.`
              : `The accountant's clients' data is subject to the same security, isolation, and retention measures described in this policy. They are not accessible to other accounts or to the ${LEGAL_NAME} team, except upon explicit legal requirement.`}
          </p>
        </div>

        {/* Sección 9: Notificación de brechas */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <AlertTriangle size={15} className="text-cyan-400 shrink-0" />
            {isEs ? 'Notificación de brechas de seguridad' : 'Security breach notification'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>En caso de producirse una brecha de seguridad que afecte datos personales,{' '}<strong style={{ color: c.textPrimary }}>{LEGAL_NAME}</strong> aplicará la siguiente política:</>
              : <>In the event of a security breach affecting personal data,{' '}<strong style={{ color: c.textPrimary }}>{LEGAL_NAME}</strong> will apply the following policy:</>}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Notificación a la ANPD:' : 'ANPD notification:'}
              </strong>{' '}
              {isEs
                ? 'dentro de las 72 horas siguientes a la detección de la brecha, cuando sea probable que entrañe un riesgo para los derechos de los titulares afectados, conforme al Art. 25 del Reglamento D.S. N° 003-2013-JUS.'
                : 'within 72 hours of detecting the breach, when it is likely to pose a risk to the rights of affected data subjects, pursuant to Art. 25 of Regulation D.S. N° 003-2013-JUS.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Notificación a usuarios afectados:' : 'Notification to affected users:'}
              </strong>{' '}
              {isEs
                ? `si el riesgo es alto, ${LEGAL_NAME} notificará por correo electrónico a la dirección registrada describiendo la naturaleza de la brecha, las categorías de datos involucrados, las medidas adoptadas y las acciones recomendadas para el titular.`
                : `if the risk is high, ${LEGAL_NAME} will notify by email to the registered address describing the nature of the breach, the categories of data involved, the measures taken, and the recommended actions for the data subject.`}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Registro interno:' : 'Internal record:'}
              </strong>{' '}
              {isEs
                ? 'toda brecha detectada queda documentada en el log de incidentes de seguridad, independientemente de si requiere notificación externa.'
                : 'every detected breach is documented in the security incident log, regardless of whether it requires external notification.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Para reportar una posible vulnerabilidad de seguridad, escribe a'
                : 'To report a possible security vulnerability, write to'}{' '}
              <a href={`mailto:${CONTACT.EMAIL_PRIVACY}`} className="text-cyan-400 hover:underline">
                {CONTACT.EMAIL_PRIVACY}
              </a>.
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="pt-6 pb-4 text-center space-y-2" style={{ borderTop: `1px solid ${c.borderSoft}` }}>
          <p className="t-sm" style={{ color: c.textSub }}>
            <strong style={{ color: c.textNormal }}>{APP_NAME}™</strong>{' '}
            · {isEs ? 'Política de Privacidad Versión 1.2' : 'Privacy Policy Version 1.2'}
          </p>
          <p className="t-2xs" style={{ color: c.textMuted }}>
            {isEs
              ? <>Vigente desde mayo 2026 · Sujeto a actualización. La versión vigente siempre estará disponible en{' '}</>
              : <>Effective since May 2026 · Subject to updates. The current version will always be available at{' '}</>}
            <a href="/politica-privacidad" className="text-cyan-400 hover:underline">
              app.tryquipus.com/politica-privacidad
            </a>
          </p>
          <p className="t-2xs mt-2" style={{ color: c.textMuted, opacity: 0.7 }}>
            {isEs
              ? 'Ley N° 29733 — Ley de Protección de Datos Personales del Perú · D.S. N° 003-2013-JUS (Reglamento)'
              : 'Law N° 29733 — Personal Data Protection Law of Peru · D.S. N° 003-2013-JUS (Regulations)'}
          </p>
          <p className="t-2xs mt-1" style={{ color: c.textMuted, opacity: 0.7 }}>
            {isEs
              ? `${LEGAL_NAME} se encuentra en proceso de registro del banco de datos personales ante el MINJUS conforme a la Ley 29733 (RNPDP en trámite).`
              : `${LEGAL_NAME} is in the process of registering the personal data bank with the MINJUS pursuant to Law 29733 (RNPDP registration pending).`}
          </p>
          <p className="t-2xs mt-1" style={{ color: c.textMuted, opacity: 0.7 }}>
            © 2026 {LEGAL_NAME}. Todos los derechos reservados. TryQuiPus™ es una plataforma operada por {LEGAL_NAME}. TryQuiPus™ es una marca comercial en proceso de registro.
          </p>
        </div>

      </div>
    </div>
  );
}
